import { accessOptions, visibilityOptions } from '../../../../domain/consts/form/template/options';
import { useCallback, useEffect, useMemo, useState } from 'react';

import TemplateForm from '../../../components/elements/forms/template-form';
import { Values } from '../../../../domain/interface/form/template';
import { createTemplate } from '../../../../store/templates/actions';
import { resetPressedState } from '../../../../store/templates/templates';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import useTemplates from '../../use-templates';
import valueTypeValidator from '../../../../domain/utils/form/template/value-type-validator';
import Modal from 'ud-ui/components/modal';
import { UDText } from 'ud-ui/components';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { templatePresets, TemplatePresetsNameList } from 'modules/template-editor/domain/consts/form/template/template-presets';

const PRESET: TemplatePresetsNameList = 'create';

function useCreateModal() {
  const dispatch = useDispatch();
  const { info } = useOrganizer();
  const { templates, isProcessed, isFinished, currentProcess } = useTemplates();
  const isAdmin = useIsAdmin();

  const location = useLocation();
  const isCatalog = location.pathname.includes('catalog');

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: Values) => {
      if (!info) {
        throw Error('failed to create template: info is undefined');
      }
      const { width, height, name, visibility, access } = valueTypeValidator({
        values,
      });
      const resolution = { width: +width, height: +height };
      dispatch(
        createTemplate({
          name,
          ownerId: info.id,
          resolution,
          createStartBoard: true,
          sportType: info.sportType,
          visibility,
          access,
        }),
      );
    },
    [dispatch, info],
  );

  useEffect(() => {
    if (!isFinished) {
      return;
    }
    if (currentProcess !== 'create template') return;
    close();
    dispatch(resetPressedState());
  }, [isProcessed, isFinished, close, dispatch, currentProcess]);

  const initialValue = useMemo(
    () => ({
      width: '1920',
      height: '1080',
      name: '',
      visibility: visibilityOptions.find((option) => option.value === 'all') ?? visibilityOptions[0],
      access: accessOptions.find((option) => option.value === (isCatalog ? 'default' : 'private')) ?? accessOptions[0],
    }),
    [isCatalog],
  );

  const existingNames = useMemo(() => templates.map((template) => template.name.trim()), [templates]);

  return {
    modal: (
      <Modal show={isOpen} onClose={close}>
        <UDText type={'title-3'} className="mb-5">
          {templatePresets[PRESET].header}
        </UDText>
        <TemplateForm
          preset={PRESET}
          initialValues={initialValue}
          onSubmit={onSubmit}
          existingNames={existingNames}
          advancedAccess={isAdmin && isCatalog}
          isLoading={isProcessed}
        />
      </Modal>
    ),
    close,
    open,
  };
}

export default useCreateModal;
